import { BlockStack, InlineStack, Text } from '@shopify/polaris';

/**
 * SVG score ring matching design mockups. Shows compliance readiness as a
 * circular progress indicator (0-100) with color-coded stroke.
 *
 * Color semantics (Polaris-aligned):
 *   ≥ 85 = success (green)
 *   ≥ 60 = warning (amber)
 *   < 60 = critical (red)
 */

const CIRCUMFERENCE = 2 * Math.PI * 46; // r=46

function strokeColor(score: number): string {
  if (score >= 85) return '#2E7D32';
  if (score >= 60) return '#F59E0B';
  return '#D72C0D';
}

export function ScoreRing({
  score,
  title,
  detail,
  disclaimer,
  deadline,
  trend,
}: {
  score: number;
  title: string;
  detail: string;
  disclaimer?: string;
  deadline?: string;
  trend?: string;
}): JSX.Element {
  const offset = CIRCUMFERENCE - (score / 100) * CIRCUMFERENCE;
  const color = strokeColor(score);

  return (
    <InlineStack gap="600" blockAlign="center" wrap={false}>
      <svg width="110" height="110" style={{ flexShrink: 0 }}>
        <circle
          cx="55" cy="55" r="46"
          fill="none"
          stroke="var(--p-color-border)"
          strokeWidth="8"
        />
        <circle
          cx="55" cy="55" r="46"
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
        />
        <text
          x="55" y="50"
          textAnchor="middle"
          fontSize="26"
          fontWeight="700"
          fill="var(--p-color-text)"
          fontFamily="Inter, sans-serif"
        >
          {score}
        </text>
        <text
          x="55" y="68"
          textAnchor="middle"
          fontSize="10"
          fill="var(--p-color-text-subdued)"
          fontFamily="Inter, sans-serif"
        >
          out of 100
        </text>
      </svg>
      <BlockStack gap="100">
        <Text as="h2" variant="headingMd">{title}</Text>
        <Text as="p" tone="subdued">{detail}</Text>
        {disclaimer && (
          <div style={{ fontSize: 12, color: '#6D7175', fontStyle: 'italic', marginTop: 4 }}>{disclaimer}</div>
        )}
        {deadline && (
          <Text as="p" variant="bodySm" fontWeight="semibold" tone="critical">
            {deadline}
          </Text>
        )}
        {trend && (
          <Text as="p" variant="bodySm" tone="subdued">
            {trend}
          </Text>
        )}
      </BlockStack>
    </InlineStack>
  );
}
